// AvailableFeaturesTab.js - Manage available features in the system
import {
  useState,
  useEffect,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Switch,
  Alert,
  DataGrid,
  IconButton,
  EditIcon,
  DeleteIcon,
  authFetch,
  authFetchRequest
} from './SettingsCommon';

import AvailableFeatureDialog from './AvailableFeatureDialog';

const AvailableFeaturesTab = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchFeatures = () => {
    setLoading(true);
    setError(null);
    authFetch('/user-roles/available-features?all=true')
      .then(data => { setFeatures(data); setLoading(false); })
      .catch(err => {
        console.error('Error fetching features:', err);
        setError('Failed to load features. Please try again.');
        setLoading(false);
      });
  };

  useEffect(() => { fetchFeatures(); }, []);

  const handleAdd = () => { setEditData(null); setOpenDialog(true); };
  const handleEdit = (feature) => { setEditData(feature); setOpenDialog(true); };

  const handleSave = async (formData) => {
    try {
      setError(null);
      let updated;
      if (editData?.id) {
        updated = await authFetchRequest(`/user-roles/available-features/${editData.id}`, formData, 'PUT');
        setFeatures(prev => prev.map(f => f.id === editData.id ? updated : f));
      } else {
        updated = await authFetchRequest('/user-roles/available-features', formData, 'POST');
        setFeatures(prev => [...prev, updated]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Error saving feature:', err);
      setError(`Failed to save feature: ${err.message}`);
      throw err;
    }
  };

  const handleDelete = async (feature) => {
    if (window.confirm(`Delete feature "${feature.label}"? This will also remove it from all roles.`)) {
      try {
        setError(null);
        await authFetchRequest(`/user-roles/available-features/${feature.id}`, null, 'DELETE');
        setFeatures(prev => prev.filter(f => f.id !== feature.id));
      } catch (err) {
        console.error('Error deleting feature:', err);
        setError(`Failed to delete feature: ${err.message}`);
      }
    }
  };

  const handleToggleActive = async (feature) => {
    try {
      setError(null);
      const updated = await authFetchRequest(`/user-roles/available-features/${feature.id}`, {
        ...feature,
        active: !feature.active
      }, 'PUT');
      setFeatures(prev => prev.map(f => f.id === feature.id ? updated : f));
    } catch (err) {
      console.error('Error toggling feature:', err);
      setError(`Failed to update feature: ${err.message}`);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'code', headerName: 'Code', width: 200 },
    { field: 'label', headerName: 'Label', width: 160 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    {
      field: 'coming_soon',
      headerName: 'Coming Soon',
      width: 110,
      renderCell: (params) => params.value ? '✓' : ''
    },
    { field: 'sort_order', headerName: 'Order', width: 70 },
    {
      field: 'active',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleActive(params.row)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(params.row); }} title="Edit Feature">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(params.row); }} title="Delete Feature" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Available Features Management</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleAdd}>Add Feature</Button>
        <Button variant="outlined" onClick={fetchFeatures} disabled={loading}>Refresh</Button>
      </Stack>

      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
        <Typography variant="body2">
          Manage the list of features available in the system. Features can be assigned to user roles via
          <strong> Manage Features</strong> on each role. A feature must also be enabled in code (constants.js)
          to be fully active — the feature flag is an additional role-level permission on top of that.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          autoHeight
          rowHeight={45}
          rows={features}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          sx={{ '& .MuiDataGrid-row': { cursor: 'default' } }}
        />
      )}

      <AvailableFeatureDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AvailableFeaturesTab;
